/**
 * LoadingContext
 *
 * Global loading state management for Race Pages.
 *
 * Loading Levels:
 * - meeting : switching meeting / venue
 * - race    : switching race
 * - page    : page-specific data loading
 * - menuTab : switching menu tabs
 *
 * Common APIs:
 * - triggerLoading(type)
 * - completeLoading(type)
 * - isLoading(type)
 *
 * Notes:
 * - Loading state is shared across all race-related components, for displaying loading effect.
 * - Loading state cannot be used on triggering data update on MyContext state.
 * - Supports minimum loading display time to avoid flashing.
 */

"use client";
import { createContext, useContext, useReducer, useCallback, useMemo } from "react";
import { pagesMeetingLevel } from "./constant/menuTabSet";

interface LoadingValue {
  race: boolean; // default true, set to false once race loading is ended.
  meeting: boolean; // default true, set to false once meeting loading is ended.
  page: boolean; // default true, set to false once page loading is ended.
  menuTab: boolean; // default true, set to false once menu tab loading is ended.
  startLoadingTime: number;
}

interface LoadingContextValue {
  loadingState: LoadingValue;
  isLoading: (type?: 'all' | 'race' | 'meeting' |'page' | 'menuTab', menuTabStr?: string) => boolean;
  triggerLoading: (type: 'all' | 'race' | 'meeting' |'page' | 'menuTab', menuTabStr?: string) => void;
  completeLoading: (type: 'race' | 'meeting' |'page' | 'menuTab') => void;
}

const loadingReducer = (state: LoadingValue, value: Partial<LoadingValue>): LoadingValue =>  {
  return {
    ...state, 
    ...value,
  };
}

export const Loading = createContext<LoadingContextValue>({} as LoadingContextValue);

interface LoadingProviderProps {
  minLoadingTime? : number;
  children?: React.ReactNode;
}

/**
 * Global loading provider for race pages.
 *
 * This provider centralizes loading state management
 * across meeting, race, page and menu-tab navigation.
 *
 * Loading hierarchy:
 * meeting
 * └─ race
 *    └─ page
 *       └─ menuTab
 *
 * Example:
 * triggerLoading('race')
 * ...
 * completeLoading('race')
 *
 * Consumers:
 * const { isLoading, triggerLoading, completeLoading } = useLoading();
 */
export function LoadingProvider({ minLoadingTime, children }: LoadingProviderProps) {
  const defaultTime = 0;
  const minTime = minLoadingTime && minLoadingTime > defaultTime ? minLoadingTime : defaultTime;
  const initialContext: LoadingValue = {
    race: true,
    meeting: true,
    page: true,
    menuTab: true,
    startLoadingTime: Date.now(),
  };
  const [state, dispatch] = useReducer(
    loadingReducer,
    initialContext
  );
  // for some pages, no need to show loading on switching race
  const isLoading = useCallback((type?: 'all' | 'race' | 'meeting' |'page' | 'menuTab', menuTabStr?: string) => {
    let loading = state.race || state.meeting || state.page || state.menuTab;
    switch (type) {
      case 'all':
        loading = state.race && state.meeting && state.page && state.menuTab;
        break;
      case 'race':
        loading = state.race || state.meeting;
        break;
      case 'meeting':
        loading = state.meeting;
        break;
      case 'page':
        loading = state.race || state.meeting || state.page;
        break;
      case 'menuTab':
        loading = state.race || state.meeting || state.menuTab;
        break;
      default:
        break;
    }
    //console.debug('LoadingContext - isLoading', Date.now(), type, menuTabStr, loading, state);
    return loading;
  }, [state]);
  const withFakeLoading = true;
  const triggerLoading = useCallback((type: 'all' | 'race' | 'meeting' |'page' | 'menuTab', menuTabStr?: string) => {
    let now = Date.now();
    let newVal: Partial<LoadingValue> = type !== 'all' ? {[type]: true, startLoadingTime: now} : {race: true, meeting: true, page: true, menuTab: true};
    switch (type) {
      case 'race':
      case 'meeting':
        const skipPageLoading = pagesMeetingLevel.includes(menuTabStr || "");
        //console.debug('LoadingContext - skipPageLoading', skipPageLoading, menuTabStr);
        if (type === 'race') {
          if (!skipPageLoading) {
            newVal.page = true;
          } else if (withFakeLoading) {
            newVal.page = true;
            setTimeout(() => {
              completeLoading('page');
            }, Math.max(minTime, 300));
          }
        }
        if (type === 'meeting') {
          newVal.race = true;
          newVal.page = true;
          newVal.menuTab = true;
        }
        break;
      case 'page':
      case 'menuTab':
      default:
        break;
    }
    //console.debug('LoadingContext - triggerLoading', menuTabStr, {...state, ...newVal});
    dispatch(newVal);
  }, [state]);
  const endLoading = (type: 'race' | 'meeting' |'page' | 'menuTab', debug: any) => {
    const newVal: Partial<LoadingValue> = {[type]: false};
    switch (type) {
      case 'race':
        newVal.meeting = false;
        break;
      case 'meeting':
      case 'page':
      case 'menuTab':
      default:
        break;
    }
    //console.debug('LoadingContext - endLoading', debug, {...state, ...newVal});
    dispatch(newVal);
  };  
  const completeLoading = useCallback((type: 'race' | 'meeting' |'page' | 'menuTab') => {
    let now = Date.now();
    const elapsed = now - state.startLoadingTime;
    const remaining = Math.max(0, minTime - elapsed);
    let debug = {type, from: state.startLoadingTime, remaining};
    //console.debug('LoadingContext - completeLoading', debug);
    if (remaining === 0){
      endLoading(type, debug);
    } else {
      setTimeout(() => endLoading(type, debug), remaining);
    }
  }, [state.startLoadingTime, minTime]);

  const value: LoadingContextValue = useMemo(() => ({loadingState: state, isLoading, triggerLoading, completeLoading}), [state, isLoading, triggerLoading, completeLoading]);

  return (
    <Loading.Provider value={value}>
      {children}
    </Loading.Provider>
  );
}

export function useLoading() {
  return useContext(Loading);
}
